import React from 'react';
import { PlaidLinkStableEvent, usePlaidLink } from 'react-plaid-link';
import _ from 'lodash';
import { ButtonBase, ButtonPrimary } from '@americanexpress/dls-react';

import { DISABLE_PLAID } from 'src/config';
import PlaidService, { PlaidMetaData } from 'src/services/plaid.service';
import { setPageLoading } from 'src/services/pageLoading.service';
import LiteralsService from 'src/services/literals/LiteralsService';
import { usePlaidToken } from 'src/hooks/queries/plaid';
import MixpanelService from 'src/services/mixpanel.service';
import UserService from 'src/services/user.service';
import { UserModel } from 'src/types/user.model';
import { AccountModel } from 'src/types/account.model';
import { trackClick } from 'src/hooks/useAdobeTags';
import { useLocation } from 'react-router-dom';
import adobeTags from 'src/types/adobe-tags';
import Selectors from 'src/utils/selectors';
import { useFeatureFlag } from 'src/hooks/queries/one-xp';
import oneXP from 'src/types/one-xp';

interface PlaidConnectorProps {
    onSuccessCallback?: () => void | Promise<void>;
    anchor?: boolean;
    className?: string;
    children?: JSX.Element | string;
    banner?: boolean;
}

/** Implementation of plaid button that does not require props or context */
export default function PlaidConnector({
    onSuccessCallback,
    anchor = false,
    banner = false,
    className,
    children = LiteralsService.getLiteral('plaidButton.connectAccounts')
}: PlaidConnectorProps): JSX.Element {
    const { data: plaidToken } = usePlaidToken();
    const { pathname } = useLocation();
    const { data: agreeAndLinkExactDateTimeFeatureFlag } = useFeatureFlag(oneXP.FeatureFlags.AgreeAndLinkExactDateTime);

    /** Get plaid onclick */
    const { open, ready } = usePlaidLink({
        token: plaidToken,
        onSuccess: (publicToken, metadata) => {
            const asyncHelper = async (): Promise<void> => {
                setPageLoading(true);
                await PlaidService.addInstitution(
                    publicToken,
                    metadata.institution.institution_id,
                    agreeAndLinkExactDateTimeFeatureFlag ? PlaidService.getAgreeDateTime() : null
                );

                const user: UserModel = await UserService.getUser();
                const numberOfAccounts: number = _.filter(user.accounts, (account: AccountModel) => {
                    return account.institution.institutionId === metadata.institution.institution_id;
                }).length;

                MixpanelService.track('Add Institution', {
                    'Number of Institutions': user.institutions ? user.institutions.length : 0,
                    'Number of Accounts': numberOfAccounts
                });

                setPageLoading(false);
                void onSuccessCallback?.();
            };
            void asyncHelper();
        },
        onEvent: (eventName, metadata) => {
            if (eventName === PlaidLinkStableEvent.OPEN) {
                MixpanelService.track('Start Plaid');
                PlaidService.setAgreeDateTime(metadata.timestamp);
            } else {
                MixpanelService.track('Plaid Event', { event: eventName, metadata });
                if (eventName === PlaidLinkStableEvent.EXIT || eventName === PlaidLinkStableEvent.ERROR)
                    void PlaidService.postUserLog(eventName, metadata as PlaidMetaData);
            }
        }
    });

    if (anchor)
        return (
            <ButtonBase
                onClick={() => {
                    trackClick(pathname, banner ? adobeTags.Banner.PlaidReconnect : adobeTags.Error.Reconnect);
                    open();
                }}
                data-cy={Selectors.General.PLAID_CONNECT}
                className={className}
            >
                {children}
            </ButtonBase>
        );

    return (
        <ButtonPrimary
            onClick={() => {
                trackClick(pathname, adobeTags.Plaid.Agree);
                open();
            }}
            disabled={!!DISABLE_PLAID || !ready}
            data-cy={Selectors.General.PLAID_CONNECT}
            className={className}
        >
            {DISABLE_PLAID ? LiteralsService.getLiteral('plaidButton.disabled') : children}
        </ButtonPrimary>
    );
}
